import { useMemo } from 'react';
import { requestStatus } from 'src/api/services/RequestClient';
import { AuthUtils } from 'src/services/Auth/Auth.utils';

const VIEWED_REQUESTS_KEY = 'viewedRequests';

interface ViewedRequest {
  id: string;
  lastViewedAt: string;
}

/**
 * Get viewed requests from localStorage
 */
const getViewedRequests = (): ViewedRequest[] => {
  try {
    const stored = localStorage.getItem(VIEWED_REQUESTS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

/**
 * Mark a request as viewed (call this when user opens the request chat)
 */
export const markRequestAsViewed = (requestId: string, lastViewedAt: string): void => {
  try {
    const viewed = getViewedRequests();
    const existing = viewed.find((v) => v.id === requestId);
    const lastViewedAtWithBuffer = new Date(new Date(lastViewedAt).getTime() + 3000); // to prevent showing unread message badge for own message
    
    if (existing) {
      existing.lastViewedAt = lastViewedAtWithBuffer.toISOString();
    } else {
      viewed.push({
        id: requestId,
        lastViewedAt: lastViewedAtWithBuffer.toISOString(),
      });
    }
    
    localStorage.setItem(VIEWED_REQUESTS_KEY, JSON.stringify(viewed));
  } catch (error) {
    console.error('Failed to mark request as viewed:', error);
  }
};

/**
 * Check if a request has unread messages
 * A request has unread messages if it was updated after the last time it was viewed
 * AND the last message was not sent by the current user
 */
export const useRequestUnreadMessages = () => {
  const hasUnreadMessages = useMemo(() => {
    return (requestId: string, updatedAt?: string | Date, status?: string, outcomesId?: string[]): boolean => {
      if (!updatedAt) return false;

      if (status === requestStatus.queued) {
        return false;
      }
      
      try {
        const viewed = getViewedRequests();
        const viewedRequest = viewed.find((v) => v.id === requestId || outcomesId?.includes(v.id));
        const updatedDate = updatedAt instanceof Date ? updatedAt : new Date(updatedAt);
        const now = new Date();
        
        // If never viewed, show badge if updated in the last 48 hours
        // (indicates recent activity/new messages)
        if (!viewedRequest) {
          const hoursSinceUpdate = (now.getTime() - updatedDate.getTime()) / (1000 * 60 * 60);
          return hoursSinceUpdate <= 48;
        }
        
        // If previously viewed, show badge if updated after last view
        const lastViewedDate = new Date(viewedRequest.lastViewedAt);
        return updatedDate > lastViewedDate;
      } catch {
        return false;
      }
    };
  }, []); // No dependencies - pure localStorage function
  
  return {
    hasUnreadMessages,
    markAsViewed: markRequestAsViewed,
  };
};
